// src/features/tasks/api/taskApi.ts
import { apiClient } from '@/config/api';
import type {
  Task,
  TaskComment,
  TaskDependency,
  TaskAuditLog,
  TaskSummary,
  SubtaskProgress,
  TaskListResult,
  TaskListFilters,
  CreateTaskDto,
  UpdateTaskDto,
  CreateCommentDto,
  DependencyType,
  AssignmentResponseInfo,
  TaskAssignmentPublicInfo,
} from '../types/task.types';

const BASE = '/api/v1/tasks';

export const taskApi = {
  // === TASK CRUD ===

  create: (data: CreateTaskDto) =>
    apiClient.post<{ task: Task }>(BASE, {
      ...data,
      engagementId: data.engagementId || data.matter_id,
      clientId: data.clientId || data.client_id,
      dueDate: data.dueDate || data.due_date,
      startDate: data.startDate || data.start_date,
      assigneeId: data.assigneeId || data.assignee_id,
      parentTaskId: data.parentTaskId || data.parent_task_id,
    }),

  getById: (taskId: string) =>
    apiClient.get<{ task: Task }>(`${BASE}/${taskId}`),

  list: (filters?: TaskListFilters) =>
    apiClient.get<TaskListResult>(BASE, filters as Record<string, unknown>),

  update: (taskId: string, data: UpdateTaskDto) =>
    apiClient.put<{ task: Task }>(`${BASE}/${taskId}`, data),

  updateStatus: (taskId: string, status: string) =>
    apiClient.patch<{ task: Task }>(`${BASE}/${taskId}/status`, { status }),

  delete: (taskId: string) =>
    apiClient.delete<void>(`${BASE}/${taskId}`),

  bulkUpdateStatus: (task_ids: string[], status: string) =>
    apiClient.patch<{ updated: number }>(`${BASE}/bulk-status`, { task_ids, status }),

  bulkReschedule: (
    engagementId: string,
    changes: Array<{ task_id: string; new_start_date: string; new_due_date: string }>,
  ) =>
    apiClient.patch<{ updated: number; tasks: Task[] }>(
      `${BASE}/bulk-reschedule`,
      {
        engagementId,
        changes: changes.map((change) => ({
          taskId: change.task_id,
          startDate: change.new_start_date,
          dueDate: change.new_due_date,
        })),
      },
    ),

  reassign: (taskId: string, assignee_id: string) =>
    apiClient.patch<{ task: Task }>(`${BASE}/${taskId}/reassign`, { assignee_id }),

  // === SUMMARY & SHORTCUTS ===

  getSummary: (matter_id?: string) =>
    apiClient.get<{ summary: TaskSummary }>(`${BASE}/summary`, matter_id ? { matter_id } : undefined),

  getOverdue: () =>
    apiClient.get<{ tasks: Task[]; total: number }>(`${BASE}/overdue`),

  getMyTasks: (filters?: { status?: string; priority?: string; search?: string; page?: number; limit?: number; sort_by?: string }) =>
    apiClient.get<TaskListResult>(`${BASE}/my`, filters as Record<string, unknown>),

  getByMatter: (matterId: string, filters?: { page?: number; limit?: number }) =>
    apiClient.get<TaskListResult>(`${BASE}/matters/${matterId}`, filters as Record<string, unknown>),

  getMatterDependencies: (matterId: string) =>
    apiClient.get<{ dependencies: TaskDependency[]; task_count: number }>(
      `${BASE}/matters/${matterId}/dependencies`,
    ),

  getByEngagement: (engagementId: string, filters?: { page?: number; limit?: number }) =>
    apiClient.get<TaskListResult>(`${BASE}/engagements/${engagementId}`, filters as Record<string, unknown>),

  getEngagementDependencies: (engagementId: string) =>
    apiClient.get<{ dependencies: TaskDependency[]; task_count: number }>(
      `${BASE}/engagements/${engagementId}/dependencies`,
    ),

  // === SUBTASKS ===

  getSubtasks: (taskId: string) =>
    apiClient.get<{ subtasks: Task[]; progress: SubtaskProgress }>(`${BASE}/${taskId}/subtasks`),

  createSubtask: (taskId: string, data: CreateTaskDto) =>
    apiClient.post<{ task: Task }>(`${BASE}/${taskId}/subtasks`, data),

  reorderSubtasks: (taskId: string, subtask_ids: string[]) =>
    apiClient.patch<{ success: boolean }>(`${BASE}/${taskId}/subtasks/reorder`, { subtask_ids }),

  promoteSubtask: (taskId: string) =>
    apiClient.patch<{ task: Task }>(`${BASE}/${taskId}/promote`, {}),

  // === COMMENTS ===

  getComments: (taskId: string) =>
    apiClient.get<{ comments: TaskComment[] }>(`${BASE}/${taskId}/comments`),

  createComment: (taskId: string, data: CreateCommentDto) =>
    apiClient.post<{ comment: TaskComment }>(`${BASE}/${taskId}/comments`, data),

  updateComment: (commentId: string, content: string) =>
    apiClient.put<{ comment: TaskComment }>(`${BASE}/comments/${commentId}`, { content }),

  deleteComment: (commentId: string) =>
    apiClient.delete<void>(`${BASE}/comments/${commentId}`),

  // === DEPENDENCIES ===

  getDependencies: (taskId: string) =>
    apiClient.get<{ dependencies: TaskDependency[]; dependedOnBy: TaskDependency[] }>(`${BASE}/${taskId}/dependencies`),

  addDependency: (taskId: string, depends_on_task_id: string, dependency_type?: DependencyType) =>
    apiClient.post<{ dependency: TaskDependency }>(`${BASE}/${taskId}/dependencies`, { depends_on_task_id, dependency_type }),

  removeDependency: (dependencyId: string) =>
    apiClient.delete<void>(`${BASE}/dependencies/${dependencyId}`),

  getAttachments: (taskId: string) =>
    apiClient.get<{
      attachments: Array<{
        id: string;
        file_name: string;
        file_size: number;
        mime_type: string;
        uploaded_by: string;
        created_at: string;
      }>;
    }>(`${BASE}/${taskId}/attachments`),

  // === AUDIT LOG ===

  getAuditLog: (taskId: string) =>
    apiClient.get<{ logs: TaskAuditLog[] }>(`${BASE}/${taskId}/audit-log`),

  // === ASSIGNMENT NOTIFICATIONS ===

  notifyAssignee: (taskId: string) =>
    apiClient.post<{ sent: boolean }>(`/api/v1/task-assignment/${taskId}/notify-assignee`, {}),

  getAssignmentStatus: (taskId: string) =>
    apiClient.get<{ assignmentResponse: AssignmentResponseInfo | null }>(`/api/v1/task-assignment/${taskId}/status`),

  // Public (no auth) — used by TaskResponsePage
  getAssignmentByToken: (token: string) =>
    apiClient.get<TaskAssignmentPublicInfo>(`/api/v1/task-assignment/public/${token}`),

  respondToAssignment: (token: string, action: 'accepted' | 'rejected', rejection_reason?: string) =>
    apiClient.post<{ success: boolean; status: string }>(`/api/v1/task-assignment/public/${token}/respond`, { action, rejection_reason }),
};
